import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Box from '../Box/Box';
import InputLabel from '../InputLabel/InputLabel';
import Icon from '../Icon/Icon';
import TCustomDropdownProps, { DropdownState, DropdownPosition } from './types/TCustomDropdownProps';
import componentName from './name';
import InputHelper from '../InputHelper';
import CustomDropdownFooter from './CustomDropdownFooter';
import camelcase from 'camelcase-es5';
import keyboardKeys, { CUSTOM_DROPDOWN_NAVIGATION_KEYS } from '../../constants/keyboardKeys';
import { TextBodySmall } from '../Text';

// Constants for better maintainability
const DROPDOWN_SELECTORS = {
  OPTIONS_CONTAINER: '[data-dropdown-options], .hz-Options-container',
  OPTIONS_CLASS: 'hz-Options-container',
} as const;

const CSS_CLASSES = {
  FOCUSED: 'is-focused',
  ERRORED: 'is-errored',
  DIRTY: 'is-dirty',
  SELECTED: 'is-selected',
  DROPDOWN_OPTION: 'hz-Custom-dropdown-option',
  DROPDOWN_TRIGGER: 'hz-dropdown-trigger',
  DROPDOWN_VALUE: 'hz-dropdown-value',
} as const satisfies Record<keyof typeof CSS_CLASSES, string>;

// Layout and positioning constants
const LAYOUT_CONSTANTS = {
  ESTIMATED_OPTION_HEIGHT: 40, // Estimated height per option in pixels
  DEFAULT_MAX_HEIGHT: 300, // Maximum dropdown height
  SPACE_BUFFER_DOWN: 30, // Buffer space when dropdown opens downward
  SPACE_BUFFER_UP: 10, // Buffer space when dropdown opens upward
  PADDING_BUFFER_WIDTH: 16, // Padding adjustment for width
} as const;

const CustomDropdown: React.FC<TCustomDropdownProps> = ({
  labelText = '',
  placeHolderText = '',
  options = [],
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  isRequired = false,
  optionalText,
  errorText,
  helperText,
  ariaLabel,
  id = `${componentName}-${camelcase(labelText)}`,
  ...rest
}) => {
  // Consolidated dropdown state
  const [dropdownState, setDropdownState] = useState<DropdownState>({
    isExpanded: false,
    isFocused: false,
    focusedOptionIndex: -1,
    openedViaKeyboard: false,
  });

  const [selectedValue, setSelectedValue] = useState(value || defaultValue || '');
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({
    top: 0,
    left: 0,
    width: 0,
    direction: 'down',
    maxHeight: 300,
  });

  const dropdownContainerRef = useRef<HTMLDivElement>(null);
  const dropdownTriggerRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const optionsContainerRef = useRef<HTMLDivElement>(null);

  // Memoized selected option label
  const selectedOptionLabel = useMemo(() => {
    const selectedOption = options.find((option) => String(option.value) === String(selectedValue));
    return selectedOption ? selectedOption.label : placeHolderText;
  }, [selectedValue, options, placeHolderText]);

  // State update helper - centralized
  const updateDropdownState = useCallback((updates: Partial<DropdownState>) => {
    setDropdownState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Helper to create synthetic events - reduces redundancy
  const createSyntheticEvent = useCallback(
    (optionValue: string | number): React.ChangeEvent<HTMLSelectElement> =>
      ({
        target: { value: String(optionValue) }, // Ensure it's always a string
      }) as unknown as React.ChangeEvent<HTMLSelectElement>,
    [],
  );

  // Helper function to check if character is alphabetic
  const isAlphabetic = useCallback((char: string): boolean => {
    return /^[a-zA-Z]$/.test(char);
  }, []);

  // Helper function to find option by first character
  const findOptionByFirstChar = useCallback(
    (char: string): number => {
      const normalizedChar = char.toLowerCase();

      // First, try to find exact match
      const matchIndex = options.findIndex(
        ({ label }) => label[0]?.toLowerCase() === normalizedChar || label[0] === char,
      );

      if (matchIndex !== -1) return matchIndex;

      // For non-alphabetic characters, return first option
      if (!isAlphabetic(char)) return 0;

      // For alphabetic characters, find next alphabetic option
      const nextIndex = options.reduce<number>((acc, { label }, idx) => {
        const firstChar = label[0]?.toLowerCase();
        return acc === -1 && firstChar > normalizedChar ? idx : acc;
      }, -1);

      return nextIndex !== -1 ? nextIndex : 0;
    },
    [options, isAlphabetic],
  );

  const focusOption = useCallback(
    (index: number) => {
      const optionElement = optionRefs.current[index];

      if (index >= 0 && index < optionRefs.current.length && optionElement) {
        updateDropdownState({ focusedOptionIndex: index });
        optionElement.focus();
      }
    },
    [updateDropdownState],
  );

  // Character-based navigation handler
  const handleCharacterNavigation = useCallback(
    (char: string) => {
      if (!dropdownState.isExpanded || options.length === 0) return;

      const targetIndex = findOptionByFirstChar(char);
      if (targetIndex >= 0 && targetIndex < options.length) {
        // Ensure we update the dropdown state first
        updateDropdownState({ focusedOptionIndex: targetIndex });
        focusOption(targetIndex);
      }
    },
    [dropdownState.isExpanded, options.length, findOptionByFirstChar, focusOption, updateDropdownState],
  );

  // Calculate next/previous index with wrapping
  const getNextIndex = useCallback(
    (currentIndex: number, direction: string) => {
      const delta = direction === keyboardKeys.ARROW_DOWN ? 1 : -1;
      return (currentIndex + delta + options.length) % options.length;
    },
    [options.length],
  );

  // Update dropdown position when expanded
  const updateDropdownPosition = useCallback(() => {
    if (!dropdownContainerRef.current) return;

    // Get the latest position of the dropdown trigger
    const rect = dropdownContainerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const estimatedListHeight = options.length * LAYOUT_CONSTANTS.ESTIMATED_OPTION_HEIGHT;

    // Determine if dropdown should open upward or downward
    const direction =
      spaceBelow < Math.min(estimatedListHeight, LAYOUT_CONSTANTS.DEFAULT_MAX_HEIGHT) && spaceAbove > spaceBelow
        ? 'up'
        : 'down';

    // Calculate max height based on available space
    const maxHeight =
      direction === 'down'
        ? Math.max(LAYOUT_CONSTANTS.DEFAULT_MAX_HEIGHT, spaceBelow - LAYOUT_CONSTANTS.SPACE_BUFFER_DOWN)
        : Math.min(LAYOUT_CONSTANTS.DEFAULT_MAX_HEIGHT, spaceAbove - LAYOUT_CONSTANTS.SPACE_BUFFER_UP);

    const actualHeight = Math.min(maxHeight, estimatedListHeight);

    setDropdownPosition({
      top: direction === 'down' ? rect.bottom + window.scrollY : rect.top + window.scrollY - actualHeight,
      left: rect.left + window.scrollX,
      width: rect.width - LAYOUT_CONSTANTS.PADDING_BUFFER_WIDTH, // Adjust for padding
      direction,
      maxHeight,
    });
  }, [options.length]);

  const handleOptionSelection = useCallback(
    (optionValue: string | number) => {
      const stringValue = String(optionValue);
      setSelectedValue(stringValue);
      updateDropdownState({
        isExpanded: false,
        focusedOptionIndex: -1,
      });

      onChange?.(createSyntheticEvent(stringValue));
      dropdownTriggerRef.current?.focus();
    },
    [onChange, updateDropdownState, createSyntheticEvent],
  );

  // Dropdown toggle handler
  const handleDropdownToggle = useCallback(
    (openedViaKeyboard = false) => {
      const newIsExpanded = !dropdownState.isExpanded;

      if (newIsExpanded) {
        // Calculate position BEFORE updating state
        updateDropdownPosition();
      }

      updateDropdownState({
        isExpanded: newIsExpanded,
        isFocused: true,
        openedViaKeyboard,
      });

      if (newIsExpanded) {
        onFocus?.(createSyntheticEvent(selectedValue));
      }
    },
    [
      dropdownState.isExpanded,
      selectedValue,
      onFocus,
      updateDropdownState,
      createSyntheticEvent,
      updateDropdownPosition,
    ],
  );

  // Update the keyboard navigation handler
  const handleKeyboardNavigation = useCallback(
    (e: React.KeyboardEvent) => {
      const { key } = e;
      const currentFocusedIndex = dropdownState.focusedOptionIndex;

      // Handle character-based navigation for single character keys (including numbers)
      if (key.length === 1 && key !== keyboardKeys.SPACE && dropdownState.isExpanded) {
        e.preventDefault();
        e.stopPropagation();
        handleCharacterNavigation(key);
        return;
      }

      // Early return if not a navigation key
      if (!CUSTOM_DROPDOWN_NAVIGATION_KEYS.includes(key as any)) return;

      // Only prevent default for non-Tab keys, or Tab when dropdown is expanded
      if (key !== keyboardKeys.TAB || dropdownState.isExpanded) {
        e.preventDefault();
      }

      switch (key) {
        case keyboardKeys.ENTER:
        case keyboardKeys.SPACE:
          if (dropdownState.isExpanded && currentFocusedIndex >= 0 && options[currentFocusedIndex]) {
            handleOptionSelection(options[currentFocusedIndex].value);
          } else if (key === keyboardKeys.SPACE) {
            handleDropdownToggle(true);
          }
          break;

        case keyboardKeys.ESCAPE:
          if (dropdownState.isExpanded) {
            updateDropdownState({ isExpanded: false, isFocused: true });
            dropdownTriggerRef.current?.focus();
          }
          break;

        case keyboardKeys.ARROW_DOWN:
        case keyboardKeys.ARROW_UP:
          if (dropdownState.isExpanded) {
            e.stopPropagation();
            if (currentFocusedIndex === -1) {
              const initialIndex = key === keyboardKeys.ARROW_DOWN ? 0 : options.length - 1;
              focusOption(initialIndex);
            } else {
              focusOption(getNextIndex(currentFocusedIndex, key));
            }
          }
          break;

        case keyboardKeys.TAB:
          if (dropdownState.isExpanded) {
            e.preventDefault();
            e.stopPropagation();

            // If no option is focused, focus the first option
            if (currentFocusedIndex === -1) {
              focusOption(0);
            }
          }
          break;
      }
    },
    [
      dropdownState,
      options,
      handleOptionSelection,
      handleDropdownToggle,
      updateDropdownState,
      focusOption,
      getNextIndex,
      handleCharacterNavigation,
    ],
  );

  // Click outside handler - optimized
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Element;

      // Check if click is within dropdown components
      if (dropdownContainerRef.current?.contains(target) || target.closest(DROPDOWN_SELECTORS.OPTIONS_CONTAINER)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      updateDropdownState({ isExpanded: false, isFocused: true });
      dropdownTriggerRef.current?.focus();
    },
    [updateDropdownState],
  );

  // Blur handler
  const handleInputBlur = useCallback(
    (event?: React.FocusEvent) => {
      const relatedTarget = event?.relatedTarget as HTMLElement;
      const optionsContainer = document.querySelector(DROPDOWN_SELECTORS.OPTIONS_CONTAINER);

      if (optionsContainer?.contains(relatedTarget) || dropdownState.isExpanded) {
        return;
      }

      updateDropdownState({ isExpanded: false, isFocused: false });
      onBlur?.(createSyntheticEvent(selectedValue));
    },
    [selectedValue, onBlur, updateDropdownState, dropdownState.isExpanded, createSyntheticEvent],
  );

  // Generate CSS classes
  const containerCssClasses = useMemo(() => {
    const classes = [''];

    if (dropdownState.isFocused || dropdownState.isExpanded) classes.push(CSS_CLASSES.FOCUSED);
    if (errorText) classes.push(CSS_CLASSES.ERRORED);
    if (selectedValue && selectedValue !== placeHolderText) classes.push(CSS_CLASSES.DIRTY);

    return classes.join(' ');
  }, [dropdownState.isFocused, dropdownState.isExpanded, errorText, selectedValue, placeHolderText]);

  // Get option CSS classes
  const getOptionCssClasses = useCallback(
    (option: any) => {
      const classes = [CSS_CLASSES.DROPDOWN_OPTION];
      if (selectedValue === option.value) classes.push(CSS_CLASSES.SELECTED);
      return classes.join(' ');
    },
    [selectedValue],
  );

  // Helper function for error/helper content
  const renderHelperOrErrorContent = useMemo(() => {
    if (errorText) return <CustomDropdownFooter errorText={errorText} />;
    if (helperText)
      return (
        <Box as="span" name="Helper-text">
          <InputHelper>{helperText}</InputHelper>
        </Box>
      );
    return null;
  }, [errorText, helperText]);

  // Focus management effect
  useEffect(() => {
    if (dropdownState.isExpanded) {
      if (optionsContainerRef.current) {
        optionsContainerRef.current.focus();

        if (dropdownState.openedViaKeyboard) {
          focusOption(0);
        } else {
          updateDropdownState({ focusedOptionIndex: -1 });
        }
      }
    } else {
      updateDropdownState({ focusedOptionIndex: -1 });
      if (dropdownTriggerRef.current && document.activeElement === dropdownTriggerRef.current) {
        dropdownTriggerRef.current.focus();
      }
    }
  }, [dropdownState.isExpanded, dropdownState.openedViaKeyboard, updateDropdownState, focusOption]);

  // Handle controlled/uncontrolled behavior
  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);

  // Click outside effect
  useEffect(() => {
    if (!dropdownState.isExpanded) return;

    document.addEventListener('click', handleClickOutside, { capture: true });
    return () => document.removeEventListener('click', handleClickOutside, { capture: true });
  }, [dropdownState.isExpanded, handleClickOutside]);

  // Restore the body scroll prevention effect
  useEffect(() => {
    if (!dropdownState.isExpanded) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [dropdownState.isExpanded]);

  // scroll effect - only when dropdown is open
  useEffect(() => {
    if (!dropdownState.isExpanded) return;

    // Update position immediately when opened
    updateDropdownPosition();

    // Handle window resize while dropdown is open
    const handleResize = () => dropdownState.isExpanded && updateDropdownPosition();

    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [dropdownState.isExpanded, updateDropdownPosition]);

  // Option click handler
  const handleOptionClick = useCallback(
    (optionValue: string | number) => (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      handleOptionSelection(optionValue);
    },
    [handleOptionSelection],
  );

  // Option focus handler
  const handleOptionFocus = useCallback(
    (index: number) => () => {
      updateDropdownState({ focusedOptionIndex: index });
    },
    [updateDropdownState],
  );

  const ariaValueText = useMemo(() => {
    const labelName = ariaLabel || labelText;
    const selectedOption = options.find((option) => String(option.value) === String(selectedValue));
    return selectedOption ? `${selectedOption.label} selected, ${labelName}, collapsed` : `${labelName}, collapsed`;
  }, [selectedValue, options, labelText, ariaLabel]);

  // Render dropdown options
  const DropdownOptions = useMemo(() => {
    if (!dropdownState.isExpanded) return null;

    return (
      <Box
        as="div"
        ref={optionsContainerRef}
        name="Custom-Options-container"
        className={DROPDOWN_SELECTORS.OPTIONS_CLASS}
        data-dropdown-options="true"
        role="listbox"
        tabIndex={-1}
        aria-labelledby={id}
        style={{
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          maxHeight: dropdownPosition.maxHeight,
          outline: 'none',
        }}
        onKeyDown={handleKeyboardNavigation}
      >
        {options.map((option, index) => (
          <Box
            as="div"
            name="Custom-Option"
            key={option.value}
            ref={(el) => {
              optionRefs.current[index] = el as HTMLDivElement | null;
            }}
            role="option"
            className={getOptionCssClasses(option)}
            aria-label={option.label}
            tabIndex={0}
            data-testid={`dropdown-option-${option.value}`}
            data-value={String(option.value)}
            onClick={handleOptionClick(option.value)}
            onFocus={handleOptionFocus(index)}
            onKeyDown={handleKeyboardNavigation}
            style={{ outline: 'none' }}
          >
            <TextBodySmall>{option.label}</TextBodySmall>
            {selectedValue === String(option.value) && <Icon name="checkmark" aria-hidden="true" />}
          </Box>
        ))}
      </Box>
    );
  }, [
    dropdownState.isExpanded,
    dropdownPosition,
    options,
    selectedValue,
    getOptionCssClasses,
    handleOptionClick,
    handleOptionFocus,
    handleKeyboardNavigation,
    id,
  ]);

  return (
    <>
      <Box as="div" name={componentName} {...rest}>
        {labelText && (
          <InputLabel htmlFor={id} isInputRequired={isRequired} labelText={labelText} optionalText={optionalText} />
        )}

        <Box ref={dropdownContainerRef} as="div" name="Custom-dropdown-container" className={containerCssClasses}>
          <Box
            id={id}
            as="div"
            ref={dropdownTriggerRef}
            name="Input"
            className={CSS_CLASSES.DROPDOWN_TRIGGER}
            role="button"
            aria-haspopup="listbox"
            aria-label={ariaValueText}
            aria-required={isRequired}
            tabIndex={0}
            data-testid="dropdown-trigger"
            data-value={selectedValue}
            onClick={() => handleDropdownToggle(false)}
            onFocus={() => updateDropdownState({ isFocused: true })}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyboardNavigation}
          >
            <span className={CSS_CLASSES.DROPDOWN_VALUE}>{selectedOptionLabel}</span>
            <Icon name={dropdownState.isExpanded ? 'arrow-up-regular' : 'arrow-down-regular'} aria-hidden="true" />
          </Box>

          {DropdownOptions && createPortal(DropdownOptions, document.body)}
        </Box>

        {renderHelperOrErrorContent}
      </Box>
    </>
  );
};

export default CustomDropdown;
